import type {
  AggregateScoreType,
  AssertionAggregation,
  CodeLanguage,
  DraftEvaluation,
  ScoreColumnLike,
  ScoreDraft,
  ScoreRow,
  ScoreTypeValue,
} from "./types";

import type {
  ScoreCard,
  ScoreMatrixCellValue,
} from "@/components/Evaluate/Reports/ScoreMatrix/types";
import type {
  ConfigureScoreRequest,
  ScoreResult,
  SmartTableAggregateResult,
} from "@/queries/smart-tables";

export const DEFAULT_TRUE_VALUES = "yes\ntrue\n1";
export const DEFAULT_FALSE_VALUES = "no\nfalse\n0";
export const MAX_CUSTOM_SCORE_CODE_LENGTH = 50_000;

const DEFAULT_SCORE_COLUMN_TYPES = new Set([
  "LLM_ASSERTION",
  "ASSERT_VALID",
  "COMPARE",
  "CONTAINS",
  "REGEX",
]);
const SCORE_LIKE_TITLE_KEYWORDS = new Set([
  "accuracy",
  "assertion",
  "correct",
  "evaluation",
  "eval",
  "judge",
  "pass",
  "passed",
  "quality",
  "score",
  "success",
]);
const TELEMETRY_TITLE_KEYWORDS = new Set([
  "cost",
  "duration",
  "latency",
  "price",
  "spend",
  "time",
  "token",
  "tokens",
]);

const SCORE_KEY_PATTERN = /\bscore\b/i;
const SCORE_MATRIX_PATTERN = /\bscore_matrix\b/i;
const SUB_SCORES_PATTERN = /\bsub_scores\b/i;
const PRIMITIVE_RETURN_PATTERN = /\breturn\s+[-+]?\d*\.?\d+\s*;?\s*$/im;

export const APPLE_FADE = { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] } as const;
export const APPLE_FADE_DELAYED = {
  duration: 0.24,
  ease: [0.25, 0.1, 0.25, 1],
} as const;

export const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const sortedUnique = (values: string[]): string[] => Array.from(new Set(values)).sort();

export const formatScore = (score: unknown): string => {
  if (score === null || score === undefined) {
    return "—";
  }

  if (typeof score === "number") {
    if (score >= 0 && score <= 1) {
      return `${(score * 100).toFixed(1)}%`;
    }
    return score.toFixed(2);
  }

  return String(score);
};

const toNumericScore = (score: unknown): number => {
  if (typeof score === "number") {
    return score;
  }
  if (typeof score === "string") {
    const parsed = Number.parseFloat(score);
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  }
  return Number.NEGATIVE_INFINITY;
};

const sortRowsByScoreDescending = (rows: ScoreRow[]): ScoreRow[] =>
  [...rows].sort((a, b) => toNumericScore(b.score) - toNumericScore(a.score));

const deriveBooleanSuccessCount = (score: unknown, totalCount: unknown): number | undefined => {
  if (typeof score !== "number" || typeof totalCount !== "number" || totalCount <= 0) {
    return undefined;
  }

  return Math.round(score * totalCount);
};

export const normalizeTokenText = (raw: string): string[] =>
  raw
    .split(/[\n,]/g)
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);

export const toTokenText = (values: unknown, fallback: string): string => {
  if (!Array.isArray(values)) {
    return fallback;
  }

  const tokens = values.map(value => String(value).trim()).filter(Boolean);
  return tokens.length > 0 ? tokens.join("\n") : fallback;
};

export const makeDefaultDraft = (columnIds: string[]): ScoreDraft => ({
  scoreType: "auto",
  selectedColumnIds: columnIds,
  trueValuesText: DEFAULT_TRUE_VALUES,
  falseValuesText: DEFAULT_FALSE_VALUES,
  assertionAggregation: "mean",
  codeLanguage: "PYTHON",
  customCode: "",
  aggregateType: "most_frequent_value",
  aggregateSourceColumnId: columnIds[0] ?? "",
  aggregateLabelColumnId: "",
});

const parseScoreType = (value: unknown): ScoreTypeValue =>
  value === "auto" ||
  value === "boolean" ||
  value === "numeric" ||
  value === "custom" ||
  value === "aggregate"
    ? value
    : "auto";

const parseAssertionAggregation = (value: unknown): AssertionAggregation =>
  value === "all" || value === "any" || value === "mean" ? value : "mean";

const parseCodeLanguage = (value: unknown): CodeLanguage =>
  value === "JAVASCRIPT" ? "JAVASCRIPT" : "PYTHON";

const parseAggregateScoreType = (value: unknown): AggregateScoreType =>
  value === "min_value" || value === "max_value" || value === "most_frequent_value"
    ? value
    : "most_frequent_value";

export const getCustomCodeWarnings = (code: string, language: CodeLanguage): string[] => {
  const trimmedCode = code.trim();
  if (!trimmedCode) {
    return [];
  }

  const warnings: string[] = [];

  if (trimmedCode.includes("```")) {
    warnings.push("Remove Markdown code fences before saving custom scorer code.");
  }

  if (!SCORE_KEY_PATTERN.test(trimmedCode)) {
    warnings.push("Return an object with a numeric `score` key.");
  }

  if (PRIMITIVE_RETURN_PATTERN.test(trimmedCode)) {
    warnings.push("Return an object (for example `{ score: 0.0 }`) instead of a primitive number.");
  }

  if (!SCORE_MATRIX_PATTERN.test(trimmedCode) && !SUB_SCORES_PATTERN.test(trimmedCode)) {
    warnings.push("Optional: include `score_matrix` or `sub_scores` for richer score debugging.");
  }

  if (language === "PYTHON" && !/\breturn\b/i.test(trimmedCode)) {
    warnings.push("Python scorer code should explicitly return a result object.");
  }

  if (language === "JAVASCRIPT" && !/\breturn\b/i.test(trimmedCode)) {
    warnings.push("JavaScript scorer code should explicitly return a result object.");
  }

  return warnings;
};

const normalizeTitleTokens = (title?: string): Set<string> => {
  if (!title) {
    return new Set();
  }

  return new Set(
    title
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );
};

const hasAnyToken = (tokens: Set<string>, keywords: Set<string>): boolean => {
  for (const token of tokens) {
    if (keywords.has(token)) {
      return true;
    }
  }
  return false;
};

const isDefaultScoreColumn = (column: ScoreColumnLike): boolean => {
  if (column.type && DEFAULT_SCORE_COLUMN_TYPES.has(column.type)) {
    return true;
  }

  const titleTokens = normalizeTitleTokens(column.title);
  if (hasAnyToken(titleTokens, TELEMETRY_TITLE_KEYWORDS)) {
    return false;
  }

  return hasAnyToken(titleTokens, SCORE_LIKE_TITLE_KEYWORDS);
};

export const getDefaultScoreColumnIds = (columns: ScoreColumnLike[]): string[] =>
  columns.filter(isDefaultScoreColumn).map(column => column.id);

export const buildDraftFromScoreConfiguration = (
  scoreConfiguration: ScoreResult["score_configuration"] | unknown,
  fallbackColumnIds: string[],
): ScoreDraft => {
  const configMeta = asRecord(scoreConfiguration);
  if (!configMeta) {
    return makeDefaultDraft(fallbackColumnIds);
  }

  const scoreConfig = asRecord(configMeta.score_config) ?? {};
  const rawColumnIds = scoreConfig.column_ids;

  const selectedColumnIds = Array.isArray(rawColumnIds)
    ? rawColumnIds.map(id => String(id))
    : fallbackColumnIds;

  return {
    scoreType: parseScoreType(configMeta.score_type),
    selectedColumnIds,
    trueValuesText: toTokenText(scoreConfig.true_values, DEFAULT_TRUE_VALUES),
    falseValuesText: toTokenText(scoreConfig.false_values, DEFAULT_FALSE_VALUES),
    assertionAggregation: parseAssertionAggregation(scoreConfig.assertion_aggregation),
    codeLanguage: parseCodeLanguage(scoreConfig.code_language),
    customCode: typeof scoreConfig.code === "string" ? scoreConfig.code : "",
    aggregateType: parseAggregateScoreType(scoreConfig.aggregation_type),
    aggregateSourceColumnId:
      typeof scoreConfig.source_column_id === "string"
        ? scoreConfig.source_column_id
        : (fallbackColumnIds[0] ?? ""),
    aggregateLabelColumnId:
      typeof scoreConfig.label_column_id === "string" ? scoreConfig.label_column_id : "",
  };
};

export const buildPayloadFromDraft = (
  source: ScoreDraft,
  defaultScoreColumnIds: string[],
): DraftEvaluation => {
  if (source.scoreType === "aggregate") {
    const sourceColumnId = source.aggregateSourceColumnId || defaultScoreColumnIds[0] || "";

    if (!sourceColumnId) {
      return {
        payload: null,
        signature: null,
        validationError: "Choose a column to summarize.",
      };
    }

    const payload: ConfigureScoreRequest = {
      score_type: "aggregate",
      score_config: {
        aggregation_type: source.aggregateType,
        source_column_id: sourceColumnId,
        label_column_id:
          source.aggregateType === "most_frequent_value" || !source.aggregateLabelColumnId
            ? null
            : source.aggregateLabelColumnId,
      },
    };

    return {
      payload,
      signature: JSON.stringify(payload),
      validationError: null,
    };
  }

  if (source.scoreType === "custom") {
    const trimmedCode = source.customCode.trim();

    if (!trimmedCode) {
      return {
        payload: null,
        signature: null,
        validationError: "Add custom scorer code to continue.",
      };
    }

    if (trimmedCode.length > MAX_CUSTOM_SCORE_CODE_LENGTH) {
      return {
        payload: null,
        signature: null,
        validationError: `Custom scorer code must be ${MAX_CUSTOM_SCORE_CODE_LENGTH.toLocaleString()} characters or fewer.`,
      };
    }

    if (trimmedCode.includes("```")) {
      return {
        payload: null,
        signature: null,
        validationError: "Remove Markdown code fences before saving custom scorer code.",
      };
    }

    const payload: ConfigureScoreRequest = {
      score_type: "custom",
      score_config: {
        code: trimmedCode,
        code_language: source.codeLanguage,
      },
    };

    return {
      payload,
      signature: JSON.stringify(payload),
      validationError: null,
      validationWarnings: getCustomCodeWarnings(trimmedCode, source.codeLanguage),
    };
  }

  const selectedColumns =
    source.selectedColumnIds.length > 0 ? source.selectedColumnIds : defaultScoreColumnIds;
  const normalizedColumns = sortedUnique(selectedColumns);

  if (normalizedColumns.length === 0) {
    return {
      payload: null,
      signature: null,
      validationError: "Select at least one score column.",
    };
  }

  if (source.scoreType === "boolean") {
    const trueValues = sortedUnique(normalizeTokenText(source.trueValuesText));
    const falseValues = sortedUnique(normalizeTokenText(source.falseValuesText));

    if (trueValues.length === 0 || falseValues.length === 0) {
      return {
        payload: null,
        signature: null,
        validationError: "Boolean scoring needs both true and false token lists.",
      };
    }

    if (trueValues.some(token => falseValues.includes(token))) {
      return {
        payload: null,
        signature: null,
        validationError: "True and false token lists must not overlap.",
      };
    }

    const payload: ConfigureScoreRequest = {
      score_type: "boolean",
      score_config: {
        column_ids: normalizedColumns,
        true_values: trueValues,
        false_values: falseValues,
        assertion_aggregation: source.assertionAggregation,
      },
    };

    return {
      payload,
      signature: JSON.stringify(payload),
      validationError: null,
    };
  }

  const payload: ConfigureScoreRequest = {
    score_type: source.scoreType,
    score_config: {
      column_ids: normalizedColumns,
    },
  };

  return {
    payload,
    signature: JSON.stringify(payload),
    validationError: null,
  };
};

export const buildSummaryRows = (scoreResult: ScoreResult | unknown): ScoreRow[] => {
  const result = asRecord(scoreResult);
  const rows: ScoreRow[] = [];

  const details = asRecord(result?.details);
  const detailColumns = details?.columns;
  const resultScoringType =
    typeof result?.scoring_type === "string" ? result.scoring_type : undefined;

  if (Array.isArray(result?.columns) && result.columns.length > 0) {
    rows.push(
      ...result.columns.map(column => {
        const safeColumn = asRecord(column) ?? {};
        const includedCount =
          typeof safeColumn.included_count === "number" ? safeColumn.included_count : undefined;
        const excludedCount =
          typeof safeColumn.excluded_count === "number" ? safeColumn.excluded_count : undefined;
        const totalCount =
          typeof includedCount === "number" && typeof excludedCount === "number"
            ? includedCount + excludedCount
            : undefined;

        const scoreType =
          typeof safeColumn.resolved_scoring_type === "string"
            ? safeColumn.resolved_scoring_type
            : resultScoringType;
        const explicitSuccessCount =
          typeof safeColumn.success_count === "number" ? safeColumn.success_count : undefined;
        const explicitTotalCount =
          typeof safeColumn.total_count === "number" ? safeColumn.total_count : undefined;
        const fallbackTotalCount = scoreType === "boolean" ? includedCount : undefined;

        return {
          key: `calc:${String(safeColumn.column_id ?? safeColumn.title ?? "unknown")}`,
          label: String(safeColumn.title ?? safeColumn.column_id ?? "Unknown"),
          score: safeColumn.score,
          scoreType,
          numSkipped:
            typeof totalCount === "number" && typeof includedCount === "number"
              ? Math.max(totalCount - includedCount, 0)
              : undefined,
          successCount:
            explicitSuccessCount ?? deriveBooleanSuccessCount(safeColumn.score, fallbackTotalCount),
          totalCount: explicitTotalCount ?? fallbackTotalCount,
        };
      }),
    );
  }

  if (rows.length === 0 && Array.isArray(detailColumns) && detailColumns.length > 0) {
    rows.push(
      ...detailColumns.map(column => {
        const safeColumn = asRecord(column) ?? {};
        const scoreType =
          typeof safeColumn.score_type === "string" ? safeColumn.score_type : resultScoringType;
        const explicitSuccessCount =
          typeof safeColumn.success_count === "number" ? safeColumn.success_count : undefined;
        const explicitTotalCount =
          typeof safeColumn.total_count === "number" ? safeColumn.total_count : undefined;

        return {
          key: `col:${String(safeColumn.column_name ?? "unknown")}`,
          label: String(safeColumn.column_name ?? "Unknown"),
          score: safeColumn.score,
          scoreType,
          numSkipped:
            typeof safeColumn.num_skipped === "number" ? safeColumn.num_skipped : undefined,
          successCount: explicitSuccessCount,
          totalCount: explicitTotalCount,
        };
      }),
    );
  }

  const subScores = asRecord(details?.sub_scores);
  if (subScores) {
    for (const [name, value] of Object.entries(subScores)) {
      rows.push({
        key: `sub:${name}`,
        label: name,
        score: value,
        scoreType: "sub-score",
      });
    }
  }

  if (rows.length > 0) {
    return sortRowsByScoreDescending(rows);
  }

  const perColumn = asRecord(result?.per_column);
  if (!perColumn) {
    return rows;
  }

  for (const [label, raw] of Object.entries(perColumn)) {
    const entry = asRecord(raw);

    rows.push({
      key: `legacy:${label}`,
      label,
      score: entry?.score ?? entry?.value ?? entry?.mean ?? entry?.accuracy ?? null,
      scoreType: typeof entry?.type === "string" ? entry.type : undefined,
      numSkipped: typeof entry?.num_skipped === "number" ? entry.num_skipped : undefined,
      successCount: typeof entry?.success_count === "number" ? entry.success_count : undefined,
      totalCount: typeof entry?.total_count === "number" ? entry.total_count : undefined,
    });
  }

  return sortRowsByScoreDescending(rows);
};

const normalizeMatrixCell = (cell: unknown): ScoreMatrixCellValue => {
  if (typeof cell === "number" || typeof cell === "string") {
    return cell;
  }

  if (cell === null) {
    return "—";
  }

  const objectCell = asRecord(cell);
  if (
    objectCell &&
    (typeof objectCell.value === "number" || typeof objectCell.value === "string")
  ) {
    return {
      value: objectCell.value,
      positive_metric:
        typeof objectCell.positive_metric === "boolean" ? objectCell.positive_metric : true,
    };
  }

  return String(cell);
};

const is2DMatrix = (value: unknown): value is unknown[][] =>
  Array.isArray(value) && value.every(row => Array.isArray(row));

const is3DMatrix = (value: unknown): value is unknown[][][] =>
  Array.isArray(value) &&
  value.every(table => Array.isArray(table) && table.every(row => Array.isArray(row)));

const normalizeScoreCard = (card: unknown[][]): ScoreCard =>
  card.map(row => row.map(cell => normalizeMatrixCell(cell)));

export const buildMatrixScoreData = (
  scoreResult: ScoreResult | unknown,
  overallScore: unknown,
  options?: { enforceCustomOnly?: boolean },
): { score: number; score_matrix: ScoreCard[] } | null => {
  const result = asRecord(scoreResult);
  const details = asRecord(result?.details);

  const scoreConfiguration = asRecord(result?.score_configuration);
  const configuredScoreType = scoreConfiguration?.score_type;
  const hasCustomScoring = result?.has_custom_scoring === true;

  if (options?.enforceCustomOnly && configuredScoreType !== "custom" && !hasCustomScoring) {
    return null;
  }

  const rawMatrix = result?.score_matrix ?? details?.matrix;

  let normalizedMatrix: ScoreCard[] | null = null;
  if (is3DMatrix(rawMatrix)) {
    normalizedMatrix = rawMatrix.map(card => normalizeScoreCard(card));
  } else if (is2DMatrix(rawMatrix)) {
    normalizedMatrix = [normalizeScoreCard(rawMatrix)];
  }

  if (!normalizedMatrix || normalizedMatrix.length === 0) {
    return null;
  }

  return {
    score: typeof overallScore === "number" ? overallScore : 0,
    score_matrix: normalizedMatrix,
  };
};

export const resolveAggregateResult = (
  scoreResult: ScoreResult | unknown,
): SmartTableAggregateResult | null => {
  const result = asRecord(scoreResult);
  const details = asRecord(result?.details);
  const detailsAggregateResult = asRecord(details?.aggregate_result);
  if (detailsAggregateResult) {
    return detailsAggregateResult as SmartTableAggregateResult;
  }

  const aggregate = asRecord(result?.aggregate);
  const aggregateResult = asRecord(aggregate?.result);
  if (aggregateResult) {
    return aggregateResult as SmartTableAggregateResult;
  }

  return null;
};

export const resolveOverallScore = (scoreResult: ScoreResult | unknown): unknown => {
  const result = asRecord(scoreResult);

  if (result?.overall_score !== null && result?.overall_score !== undefined) {
    return result.overall_score;
  }

  if (result?.aggregate_score !== null && result?.aggregate_score !== undefined) {
    return result.aggregate_score;
  }

  const aggregate = asRecord(result?.aggregate);
  return aggregate?.score ?? aggregate?.accuracy ?? aggregate?.mean ?? aggregate?.value ?? null;
};
